import { useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import "./AccountProfile.css";

import phoneCountries from "../../catalogs/phoneCountries";
import {
  updatePerfilCliente,
  changePasswordCliente,
} from "../../services/usuarios_cliente/perfilcliente";

import {
  getErrMsg,
  onlyDigits,
  shallowEqualByKeys,
  norm,
  getPhoneMeta,
  validatePhone,
} from "../../utils/accountUtils";
import {
  getPasswordChecks,
  getPasswordStrength,
  isStrongPasswordValid,
  STRONG_PASSWORD_ERROR,
} from "../../utils/passwordRules";

export default function AccountProfile() {
  const { profile, setProfile, loadingPerfil, pushToast } = useOutletContext();

  const baselineRef = useRef(null);

  const [savingProfile, setSavingProfile] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
  });

  const phoneMeta = useMemo(
    () => getPhoneMeta(profile?.codigo_telefono),
    [profile?.codigo_telefono]
  );

  const handleProfileChange = (e) => {
    const { name, value } = e.target;

    if (name === "telefono") {
      setProfile((p) => ({ ...p, telefono: onlyDigits(value) }));
      return;
    }

    setProfile((p) => ({ ...p, [name]: value }));
  };

  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPasswords((p) => ({ ...p, [name]: value }));
  };

  const isDirty = useMemo(() => {
    const base = baselineRef.current;
    if (!base) return false;

    const coreChanged = !shallowEqualByKeys(
      {
        nombre: base.nombre,
        apellido: base.apellido,
        correo: base.correo,
        codigo_telefono: base.codigo_telefono,
        telefono: base.telefono,
      },
      {
        nombre: profile.nombre,
        apellido: profile.apellido,
        correo: profile.correo,
        codigo_telefono: profile.codigo_telefono,
        telefono: profile.telefono,
      },
      ["nombre", "apellido", "correo", "codigo_telefono", "telefono"]
    );

    const wantsPassword = norm(passwords.currentPassword) !== "" || norm(passwords.newPassword) !== "";
    return coreChanged || wantsPassword;
  }, [
    profile.nombre,
    profile.apellido,
    profile.correo,
    profile.codigo_telefono,
    profile.telefono,
    passwords.currentPassword,
    passwords.newPassword,
  ]);

  const saveProfile = async () => {
    if (!isDirty) {
      pushToast("info", "Sin cambios", "No hay nada nuevo para guardar.");
      return;
    }

    setSavingProfile(true);
    try {
      const base = baselineRef.current || {
        nombre: "",
        apellido: "",
        correo: "",
        codigo_telefono: "",
        telefono: "",
      };

      const coreChanged = !shallowEqualByKeys(
        {
          nombre: base.nombre,
          apellido: base.apellido,
          correo: base.correo,
          codigo_telefono: base.codigo_telefono,
          telefono: base.telefono,
        },
        {
          nombre: profile.nombre,
          apellido: profile.apellido,
          correo: profile.correo,
          codigo_telefono: profile.codigo_telefono,
          telefono: profile.telefono,
        },
        ["nombre", "apellido", "correo", "codigo_telefono", "telefono"]
      );

      if (coreChanged) {
        const phoneCheck = validatePhone({
          codigo_telefono: profile.codigo_telefono,
          telefono: profile.telefono,
        });

        if (!phoneCheck.ok) {
          pushToast("warn", "Teléfono inválido", phoneCheck.message || "Revisa tu número.");
          return;
        }

        await updatePerfilCliente({
          nombre: profile.nombre,
          apellido: profile.apellido,
          correo: profile.correo,
          codigo_telefono: phoneCheck.clean.code || null,
          telefono: phoneCheck.clean.phone || null,
        });

        baselineRef.current = {
          nombre: norm(profile.nombre),
          apellido: norm(profile.apellido),
          correo: norm(profile.correo),
          codigo_telefono: norm(profile.codigo_telefono),
          telefono: norm(profile.telefono),
        };
      }

      const wantsPassword = norm(passwords.currentPassword) !== "" || norm(passwords.newPassword) !== "";

      if (wantsPassword) {
        if (!norm(passwords.currentPassword) || !norm(passwords.newPassword)) {
          pushToast(
            "warn",
            "Faltan campos de contraseña",
            "Ingresa tu contraseña actual y la nueva."
          );
          return;
        }

        if (!isStrongPasswordValid(passwords.newPassword)) {
          pushToast("warn", "Contraseña insegura", STRONG_PASSWORD_ERROR);
          return;
        }

        await changePasswordCliente({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
        });

        setPasswords({ currentPassword: "", newPassword: "" });
      }

      pushToast("success", "Cambios guardados", "Tu perfil se actualizó correctamente.");
    } catch (error) {
      pushToast("error", "Error al guardar", getErrMsg(error, "No pudimos guardar tu perfil."));
    } finally {
      setSavingProfile(false);
    }
  };

  // set baseline cuando el perfil ya cargó
  useMemo(() => {
    if (!loadingPerfil && baselineRef.current === null) {
      baselineRef.current = {
        nombre: norm(profile.nombre),
        apellido: norm(profile.apellido),
        correo: norm(profile.correo),
        codigo_telefono: norm(profile.codigo_telefono),
        telefono: norm(profile.telefono),
      };
    }
    return null;
  }, [
    loadingPerfil,
    profile.nombre,
    profile.apellido,
    profile.correo,
    profile.codigo_telefono,
    profile.telefono,
  ]);

  const pwChecks = getPasswordChecks(passwords.newPassword || "");
  const pwStrength = getPasswordStrength(passwords.newPassword || "");
  const showPwFeedback = (passwords.newPassword || "").length > 0;
  const pwRules = [
    ["8+ caracteres", pwChecks.length],
    ["Mayúscula", pwChecks.upper],
    ["Minúscula", pwChecks.lower],
    ["Número", pwChecks.number],
    ["Símbolo", pwChecks.symbol],
  ];

  return (
    <div className="adc-card adc-profile">
      {loadingPerfil && <p className="muted">Cargando perfil…</p>}

      {!loadingPerfil && (
        <>
          <div className="adc-profile-head">
            <div>
              <h2 className="adc-profile-title">Perfil</h2>
              <p className="muted">Actualiza tus datos personales y contraseña.</p>
            </div>
          </div>

          <div className="adc-form-grid">
            <div className="adc-field">
              <label>Nombre</label>
              <input name="nombre" value={profile.nombre} onChange={handleProfileChange} />
            </div>

            <div className="adc-field">
              <label>Apellido</label>
              <input name="apellido" value={profile.apellido} onChange={handleProfileChange} />
            </div>

            <div className="adc-field">
              <label>Correo</label>
              <input name="correo" value={profile.correo} onChange={handleProfileChange} />
            </div>

            <div className="adc-field adc-field--full">
              <label>Teléfono</label>

              <div className="adc-phoneRow">
                <select
                  className="adc-input"
                  name="codigo_telefono"
                  value={profile.codigo_telefono}
                  onChange={handleProfileChange}
                >
                  {phoneCountries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.country} ({c.code})
                    </option>
                  ))}
                </select>

                <input
                  className="adc-input"
                  name="telefono"
                  inputMode="numeric"
                  placeholder={
                    phoneMeta?.minLength === phoneMeta?.maxLength
                      ? `${phoneMeta.maxLength} dígitos`
                      : `${phoneMeta.minLength}-${phoneMeta.maxLength} dígitos`
                  }
                  value={profile.telefono}
                  onChange={handleProfileChange}
                />
              </div>

              <div className="adc-field-help">
                Formato: <strong>{profile.codigo_telefono}</strong> +{" "}
                <strong>
                  {phoneMeta?.minLength === phoneMeta?.maxLength
                    ? `${phoneMeta.maxLength}`
                    : `${phoneMeta.minLength}–${phoneMeta.maxLength}`}
                </strong>{" "}
                dígitos (solo números).
              </div>
            </div>
          </div>

          <hr className="adc-divider" />

          <h3 className="adc-subsection">Cambiar contraseña</h3>

          <div className="adc-form-grid">
            <div className="adc-field">
              <label>Contraseña actual</label>
              <input
                type="password"
                name="currentPassword"
                value={passwords.currentPassword}
                onChange={handlePassChange}
              />
            </div>

            <div className="adc-field">
              <label>Nueva contraseña</label>
              <input
                type="password"
                name="newPassword"
                value={passwords.newPassword}
                onChange={handlePassChange}
              />

              {showPwFeedback && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
                    {[1, 2, 3].map((seg) => (
                      <div
                        key={seg}
                        style={{
                          flex: 1,
                          height: 5,
                          borderRadius: 4,
                          background: pwStrength.level >= seg ? pwStrength.color : "#e5e7eb",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: pwStrength.color, marginBottom: 6 }}>
                    Seguridad: {pwStrength.label}
                  </div>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "4px 12px",
                    }}
                  >
                    {pwRules.map(([texto, ok]) => (
                      <li
                        key={texto}
                        style={{ fontSize: 12, color: ok ? "#16a34a" : "#9ca3af", display: "flex", gap: 6 }}
                      >
                        <span style={{ fontWeight: 800 }}>{ok ? "✓" : "○"}</span>
                        {texto}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="adc-profile-actions">
            <button
              type="button"
              className="adc-btn-primary"
              onClick={saveProfile}
              disabled={savingProfile || !isDirty}
              title={!isDirty ? "No hay cambios para guardar" : ""}
            >
              {savingProfile ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}